import React, { useEffect, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import axios from "axios";
import { API, useAuth } from "@/auth";

/* ============================================================
 /studio/shelf — THE SHELF

 A writer's personal reference library. Add songs you love
 (artist + title + optional note) OR just a free-text vibe.
 Claude reads each one and stores a Suno-safe sonic descriptor
 — real names get stripped so they never trip the filter.

 On /charts/submit, one-tap "USE MY SHELF" merges the whole
 shelf into a single VIBE line for the next render.

 DJ Neo also reads the shelf when giving feedback, so his
 notes reflect the writer's actual taste.
============================================================ */

export default function Shelf() {
 const { user, loading: authLoading } = useAuth(); const navigate = useNavigate();
 const [refs, setRefs] = useState([]);
 const [loading, setLoading] = useState(true);
 const [artist, setArtist] = useState("");
 const [title, setTitle] = useState("");
 const [note, setNote] = useState("");
 const [busy, setBusy] = useState(false);
 const [err, setErr] = useState("");
 const [merged, setMerged] = useState({ vibe: "", used_count: 0 });
 const [mergeBusy, setMergeBusy] = useState(false);

 useEffect(() => {
  if (authLoading) return; // auth check still in flight
  if (!user) { navigate("/login?next=/studio/shelf"); return; }
  loadRefs();
  // eslint-disable-next-line
 }, [authLoading, user]);

 const loadRefs = async () => {
  setLoading(true);
  try {
   const { data } = await axios.get(`${API}/references`);
   setRefs(data.references || []);
  } catch (e) {
   setErr(e?.response?.data?.detail || "Couldn't load the shelf.");
  } finally {
   setLoading(false);
  }
 };

 const addRef = async (e) => {
  e?.preventDefault?.();
  if (busy) return;
  const a = artist.trim(), t = title.trim(), n = note.trim();
  if (!a && !t && !n) { setErr("Give me an artist, a song, or a note — at least one."); return; }
  setErr(""); setBusy(true);
  try {
   const { data } = await axios.post(`${API}/references`, {
    artist: a || null, title: t || null, note: n || null,
   });
   setRefs(prev => [data.reference, ...prev]);
   setArtist(""); setTitle(""); setNote("");
   // Invalidate cached merge — next USE MY SHELF click will recompute
   setMerged({ vibe: "", used_count: 0 });
  } catch (e2) {
   setErr(e2?.response?.data?.detail || "Couldn't save that reference.");
  } finally { setBusy(false); }
 };

 const removeRef = async (id) => {
  try {
   await axios.delete(`${API}/references/${id}`);
   setRefs(prev => prev.filter(r => r.id !== id));
   setMerged({ vibe: "", used_count: 0 });
  } catch (e) {
   setErr(e?.response?.data?.detail || "Couldn't remove that reference.");
  }
 };

 const computeMerge = async () => {
  setMergeBusy(true); setErr("");
  try {
   const { data } = await axios.get(`${API}/references/merged-vibe`);
   setMerged(data);
  } catch (e) {
   setErr(e?.response?.data?.detail || "Merge failed — try again.");
  } finally { setMergeBusy(false); }
 };

 // OpenPersona v0.1 — export the whole user profile (identity + shelf +
 // projects + preferences) as a portable, user-owned JSON file. The
 // spec lives at /openpersona.
 const exportPersona = async () => {
  try {
   const res = await axios.get(`${API}/persona/export`, { responseType: "blob" });
   const url = URL.createObjectURL(res.data);
   const a = document.createElement("a");
   a.href = url;
   a.download = `${user?.rapper_name || "me"}.openpersona.json`;
   document.body.appendChild(a);
   a.click();
   a.remove();
   URL.revokeObjectURL(url);
  } catch (e) {
   setErr(e?.response?.data?.detail || "Export failed.");
  }
 };

 const importPersona = async (file) => {
  if (!file) return;
  setErr("");
  try {
   const text = await file.text();
   const persona = JSON.parse(text);
   const { data } = await axios.post(`${API}/persona/import`, { persona });
   const added = data?.imported?.shelf_refs || 0;
   const skipped = data?.imported?.shelf_refs_skipped_capacity || 0;
   let msg = `Imported ${added} reference${added === 1 ? "" : "s"} from persona.`;
   if (skipped > 0) msg += ` (${skipped} skipped — shelf full.)`;
   alert(msg);
   await loadRefs();
   setMerged({ vibe: "", used_count: 0 });
  } catch (e) {
   setErr(e?.response?.data?.detail || "Import failed — is that a valid OpenPersona file?");
  }
 };

 if (authLoading || loading) {
  return (
   <div className="min-h-screen bg-black text-white flex items-center justify-center">
    <div className="font-mono text-xs text-yt-muted animate-pulse">LOADING SHELF…</div>
   </div>
  );
 }

 return (
  <div data-testid="shelf-page" className="min-h-screen bg-black text-white">
   {/* Header */}
   <section className="border-b border-yt-border">
    <div className="max-w-4xl mx-auto px-4 md:px-8 py-8 md:py-12">
     <div className="font-mono text-[10px] tracking-widest text-yt-yellow mb-2">STUDIO · REFERENCE LIBRARY</div>
     <h1 className="font-display text-4xl sm:text-5xl lg:text-6xl leading-none">
      THE <span className="text-yt-yellow">SHELF</span>
     </h1>
     <p className="mt-3 font-mono text-sm text-white/80 max-w-2xl leading-relaxed">
      Songs and vibes you want the AI to learn from. Drop in what you love — the actual band, the actual song, or just describe the feel in your own words. I translate every entry into producer-language so real names never trip Suno's filter.
     </p>
     <p className="mt-2 font-mono text-xs text-yt-muted">
      The more you feed the shelf, the closer every new render lands to what's in your head.
     </p>
    </div>
   </section>

   <div className="max-w-4xl mx-auto px-4 md:px-8 py-8 space-y-8">

    {/* OPENPERSONA — portable export/import. Reference implementation
        of the spec at /openpersona. This is what makes The Shelf
        (and this whole platform) portable to other AI tools. */}
    <section data-testid="openpersona-actions" className="border border-yt-yellow/60 bg-black p-4 flex items-center gap-3 flex-wrap">
     <div className="flex-1 min-w-[220px]">
      <div className="font-display text-sm tracking-widest text-yt-yellow">OPENPERSONA v0.1</div>
      <div className="font-mono text-[10px] text-white/70 mt-0.5">
       Your shelf + identity as a portable file. Take it to any AI, bring one back. <Link to="/openpersona" className="text-yt-yellow underline">what is this?</Link>
      </div>
     </div>
     <button
      type="button"
      data-testid="shelf-export-persona"
      onClick={exportPersona}
      className="font-mono text-[11px] tracking-widest px-3 py-2 border border-yt-yellow text-yt-yellow hover:bg-yt-yellow hover:text-black"
     >
      ▼ EXPORT
     </button>
     <label className="font-mono text-[11px] tracking-widest px-3 py-2 border border-yt-yellow text-yt-yellow hover:bg-yt-yellow hover:text-black cursor-pointer">
      ▲ IMPORT
      <input
       data-testid="shelf-import-persona"
       type="file"
       accept="application/json,.json"
       onChange={(e) => { const f = e.target.files?.[0]; importPersona(f); e.target.value = ""; }}
       className="hidden"
      />
     </label>
    </section>

    {/* ADD FORM */}
    <section data-testid="shelf-add-form" className="border-2 border-yt-yellow p-5 bg-yt-surface">
     <div className="font-display text-xl tracking-widest text-yt-yellow mb-3">ADD A REFERENCE</div>
     <form onSubmit={addRef} className="space-y-3">
      <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
       <div>
        <label className="font-mono text-[10px] tracking-widest text-yt-muted">ARTIST <span className="normal-case text-yt-muted">(optional)</span></label>
        <input
         data-testid="shelf-artist-input"
         type="text"
         value={artist}
         onChange={e => setArtist(e.target.value)}
         maxLength={80}
         placeholder="e.g. Sublime · Kendrick Lamar · Kacey Musgraves"
         className="w-full bg-black border-2 border-yt-border focus:border-yt-yellow px-3 py-2 font-mono text-sm outline-none mt-1"
        />
       </div>
       <div>
        <label className="font-mono text-[10px] tracking-widest text-yt-muted">SONG <span className="normal-case text-yt-muted">(optional)</span></label>
        <input
         data-testid="shelf-title-input"
         type="text"
         value={title}
         onChange={e => setTitle(e.target.value)}
         maxLength={120}
         placeholder="e.g. Doin' Time · Alright · Slow Burn"
         className="w-full bg-black border-2 border-yt-border focus:border-yt-yellow px-3 py-2 font-mono text-sm outline-none mt-1"
        />
       </div>
      </div>
      <div>
       <label className="font-mono text-[10px] tracking-widest text-yt-muted">
        WHAT YOU LOVE ABOUT IT <span className="normal-case text-yt-muted">(optional · or use this alone to describe a vibe with no specific song)</span>
       </label>
       <textarea
        data-testid="shelf-note-input"
        value={note}
        onChange={e => setNote(e.target.value)}
        maxLength={280}
        rows={2}
        placeholder="e.g. love the porch feel and the lazy vocal · gravelly hip-hop singer voice · dark trap with airy synths"
        className="w-full bg-black border-2 border-yt-border focus:border-yt-yellow px-3 py-2 font-mono text-sm outline-none resize-y mt-1"
       />
       <div className="text-right font-mono text-[10px] text-yt-muted mt-1">{note.length}/280</div>
      </div>
      {err && (
       <div data-testid="shelf-error" className="border-l-4 border-yt-red bg-yt-red/10 p-2 font-mono text-xs text-yt-red">
        {err}
       </div>
      )}
      <button
       type="submit"
       data-testid="shelf-add-btn"
       disabled={busy}
       className="w-full bg-yt-yellow text-black font-display text-lg tracking-widest py-3 disabled:opacity-40 hover:bg-white transition-colors"
      >
       {busy ? "READING THE REFERENCE…" : "ADD TO SHELF"}
      </button>
     </form>
    </section>

    {/* MERGED VIBE PREVIEW */}
    <section className="border-2 border-yt-border p-5 bg-yt-surface">
     <div className="flex items-start justify-between gap-4 flex-wrap">
      <div className="flex-1 min-w-0">
       <div className="font-display text-xl tracking-widest text-white">YOUR MERGED VIBE</div>
       <div className="font-mono text-[10px] text-yt-muted mt-1">
        This is the single VIBE line USE MY SHELF drops into the Studio at render time.
       </div>
      </div>
      <button
       type="button"
       data-testid="shelf-merge-btn"
       onClick={computeMerge}
       disabled={mergeBusy || refs.length === 0}
       className="border-2 border-yt-yellow text-yt-yellow font-mono text-xs tracking-widest px-3 py-2 hover:bg-yt-yellow hover:text-black disabled:opacity-40 disabled:cursor-not-allowed"
      >
       {mergeBusy ? "MERGING…" : (refs.length === 0 ? "ADD REFS FIRST" : "COMPUTE MERGE")}
      </button>
     </div>
     {merged.vibe ? (
      <div data-testid="shelf-merged-vibe" className="mt-3 border-l-4 border-yt-yellow bg-black p-3 font-mono text-sm text-white/90 leading-relaxed">
       {merged.vibe}
       <div className="font-mono text-[10px] text-yt-muted mt-2">Synthesized from {merged.used_count} reference{merged.used_count === 1 ? "" : "s"}.</div>
      </div>
     ) : (
      <div className="mt-3 font-mono text-xs text-yt-muted italic">
       {refs.length === 0 ? "Add at least one reference above, then merge." : "Tap COMPUTE MERGE to synthesize your shelf into one Suno-safe vibe line."}
      </div>
     )}
    </section>

    {/* SHELF LIST */}
    <section>
     <div className="flex items-center justify-between mb-3">
      <div className="font-display text-2xl tracking-widest text-white">
       YOUR REFS <span className="text-yt-yellow">({refs.length})</span>
      </div>
      <Link
       to="/charts/submit"
       data-testid="shelf-go-to-studio"
       className="font-mono text-xs tracking-widest text-yt-yellow hover:underline"
      >
       → BACK TO STUDIO
      </Link>
     </div>

     {refs.length === 0 ? (
      <div data-testid="shelf-empty" className="border-2 border-dashed border-yt-border p-8 text-center">
       <div className="font-mono text-sm text-yt-muted">
        Shelf's empty, brother. Drop in the first song you'd play me if we were in the same room.
       </div>
      </div>
     ) : (
      <div className="grid grid-cols-1 gap-3">
       {refs.map((r, i) => (
        <div
         key={r.id}
         data-testid={`shelf-ref-${i}`}
         className="border-2 border-yt-border p-4 bg-yt-surface hover:border-yt-yellow/60 transition-colors"
        >
         <div className="flex items-start justify-between gap-3">
          <div className="flex-1 min-w-0">
           <div className="font-display text-lg tracking-wide truncate">
            {r.artist && r.title ? (
             <><span className="text-yt-yellow">{r.artist}</span> <span className="text-yt-muted">·</span> {r.title}</>
            ) : r.artist ? (
             <span className="text-yt-yellow">{r.artist}</span>
            ) : r.title ? (
             r.title
            ) : (
             <span className="text-white/80 italic">Vibe reference</span>
            )}
           </div>
           {r.note && (
            <div className="font-mono text-xs text-white/70 mt-1">
             <span className="text-yt-muted">Note:</span> {r.note}
            </div>
           )}
          </div>
          <button
           type="button"
           data-testid={`shelf-remove-${i}`}
           onClick={() => removeRef(r.id)}
           className="font-mono text-[10px] tracking-widest text-yt-muted hover:text-yt-red border border-yt-border hover:border-yt-red px-2 py-1 shrink-0"
          >
           REMOVE
          </button>
         </div>
         {r.descriptor && (
          <div className="mt-3 border-l-4 border-yt-yellow bg-black p-2 font-mono text-xs text-white/80 leading-relaxed">
           <span className="text-yt-yellow">SUNO-SAFE:</span> {r.descriptor}
          </div>
         )}
        </div>
       ))}
      </div>
     )}
    </section>

   </div>
  </div>
 );
}
